import {
    Avatar,
    Button,
    Divider,
    Flexer,
    Heading,
    Icon,
    Popover,
    Portal,
    Sidebar,
    Stack,
    Text,
    Tooltip,
    TooltipContent,
    View,
    stopEvent,
    useInput,
    useVisibility,
} from '@fold-dev/core'
import { useEffect, useState } from 'react'
import { TbArchive, TbLayoutGrid, TbPlus, TbTag, TbUserSquareRounded } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { Block, Loading } from '../components'
import { byName, logger } from '../helpers/util'
import { useProjects, useWorkspaces } from '../hooks/api.hooks'
import { useAuth } from '../hooks/auth.hook'
import { Logo, LogoF, LogoNew } from '../logo'
import { AccountModal } from '../modals/account.modal'
import { InboxModal } from '../modals/inbox.modal'
import { LabelsModal } from '../modals/labels.modal'
import { LikesModal } from '../modals/likes.modal'
import { MyCalendarModal } from '../modals/my-calendar.modal'
import { MyTasksModal } from '../modals/my-tasks.modal'
import ProjectsModal from '../modals/projects.modal'
import { WorkspaceMembersModal } from '../modals/workspace-members.modal'
import WorkspaceOnboardingModal from '../modals/workspace-onboarding.modal'
import { WorkspaceModal } from '../modals/workspace.modal'
import { useAppStore } from '../store/app'
import { StarredList } from './nav/starred'
import { PresenceAvatar } from './common/presence-avatar'

export const Archives = () => {
    const navigate = useNavigate()
    const { workspaceId, projectId } = useParams()
    const { getAll } = useWorkspaces()
    const [workspaces, setWorkspaces] = useState([])
    const workspace = useAppStore((state) => state.workspace)
    const user = useAppStore((state) => state.user)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>('')
    const labelsModal = useVisibility()
    const onboardingModal = useVisibility()
    const inboxModal = useVisibility()
    const workspaceModal = useVisibility()
    const workspaceMembersModal = useVisibility()
    const projectsModal = useVisibility()
    const profileMenu = useVisibility()
    const myTasksModal = useVisibility()
    const myCalendarModal = useVisibility()
    const likesModal = useVisibility()
    const accountModal = useVisibility()
    const { getMe, logout } = useAuth()
    const [panel, setPanel] = useState(0)
    const [type, setType] = useState('')
    const name = useInput('')
    const [color, setColor] = useState('')
    const projectsAPI = useProjects()
    const add = useVisibility()
    const archives = useVisibility()
    const [projects, setProjects] = useState([])
    const actions = useAppStore((state) => state.actions)

    const handleRestore = async ({ id, teamId }) => {
        setLoading(true)

        await projectsAPI.update(workspaceId, id, { archived: false })
        const project = await projectsAPI.getOne(workspaceId, id)

        actions.projectAdd(project)
        actions.projectUpdate({ id, archived: false })

        setLoading(false)
        setProjects(projects.filter((project) => project.id != id))
    }

    const fetchArchivedProjects = async () => {
        setLoading(true)
        setError(null)

        try {
            const projects = await projectsAPI.getArchived(workspaceId)

            setLoading(false)
            setProjects(projects)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    return (
        <Popover
            arrow
            anchor="top-center"
            width="fit-content"
            portal={Portal}
            isVisible={archives.visible}
            onDismiss={archives.hide}
            content={
                <div
                    onMouseDown={stopEvent}
                    onClick={stopEvent}>
                    <Loading loading={loading} />
                    <Stack
                        spacing="0.8rem"
                        direction="vertical"
                        width={250}
                        p="1.5rem"
                        style={{
                            maxHeight: 300,
                            overflowY: 'auto',
                        }}>
                        {!!projects.length && (
                            <Heading
                                as="h6"
                                fontWeight={600}
                                colorToken="text-weakest">
                                Archived Projects
                            </Heading>
                        )}
                        {projects.map((project, index) => (
                            <View
                                row
                                width="100%"
                                key={index}>
                                <Text
                                    className="f-buttonize"
                                    onClick={() => {
                                        archives.hide()
                                        navigate(`/app/workspace/${workspaceId}/c/${project.id}/list`)
                                    }}
                                    flex={1}>
                                    {project.name}
                                </Text>
                                <Button
                                    style={{ outline: 'none' }}
                                    size="sm"
                                    height="1.25rem"
                                    p="0 0.5rem"
                                    onClick={() => {
                                        archives.hide()
                                        handleRestore(project)
                                    }}>
                                    Restore
                                </Button>
                            </View>
                        ))}
                        {!projects.length && <Text colorToken="text-weaker">You have no archived projects.</Text>}
                    </Stack>
                </div>
            }>
            <View>
                <Tooltip text="Archived">
                    <Button
                        subtle
                        size="xs"
                        onClick={() => {
                            fetchArchivedProjects()
                            archives.show()
                        }}>
                        <Icon icon={TbArchive} />
                    </Button>
                </Tooltip>
            </View>
        </Popover>
    )
}

export const Dock = (props: any) => {
    const navigate = useNavigate()
    const { workspaceId, projectId } = useParams()
    const { getAll } = useWorkspaces()
    const [workspaces, setWorkspaces] = useState([])
    const workspace = useAppStore((state) => state.workspace)
    const user = useAppStore((state) => state.user)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>('')
    const labelsModal = useVisibility()
    const onboardingModal = useVisibility()
    const inboxModal = useVisibility()
    const workspaceModal = useVisibility()
    const workspaceMembersModal = useVisibility()
    const projectsModal = useVisibility()
    const profileMenu = useVisibility()
    const myTasksModal = useVisibility()
    const myCalendarModal = useVisibility()
    const likesModal = useVisibility()
    const accountModal = useVisibility()
    const { getMe, logout } = useAuth()
    const [panel, setPanel] = useState(0)
    const [type, setType] = useState('')
    const name = useInput('')
    const [color, setColor] = useState('')
    const projectsAPI = useProjects()
    const add = useVisibility()
    const archives = useVisibility()
    const [projects, setProjects] = useState([])
    const actions = useAppStore((state) => state.actions)
    const hideNav = useAppStore((state) => state.hideNav)

    const handleRestore = async ({ id, teamId }) => {
        setLoading(true)

        await projectsAPI.update(workspaceId, id, { archived: false })
        const project = await projectsAPI.getOne(workspaceId, id)

        actions.projectAdd(project)
        actions.projectUpdate({ id, archived: false })

        setLoading(false)
        setProjects(projects.filter((project) => project.id != id))
    }

    const fetchArchivedProjects = async () => {
        setLoading(true)
        setError(null)

        try {
            const projects = await projectsAPI.getArchived(workspaceId)

            setLoading(false)
            setProjects(projects)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const workspaces = await getAll()

            setWorkspaces(workspaces?.sort(byName))
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    return (
        <>
            {workspaceMembersModal.visible && <WorkspaceMembersModal onDismiss={workspaceMembersModal.hide} />}

            {accountModal.visible && <AccountModal onDismiss={accountModal.hide} />}

            {myCalendarModal.visible && <MyCalendarModal onDismiss={myCalendarModal.hide} />}

            {myTasksModal.visible && <MyTasksModal onDismiss={myTasksModal.hide} />}

            {likesModal.visible && <LikesModal onDismiss={likesModal.hide} />}

            {inboxModal.visible && <InboxModal onDismiss={inboxModal.hide} />}

            {workspaceModal.visible && <WorkspaceModal onDismiss={workspaceModal.hide} />}

            {labelsModal.visible && <LabelsModal onDismiss={labelsModal.hide} />}

            {projectsModal.visible && (
                <ProjectsModal
                    teamName="Workspace"
                    teamId={null}
                    onDismiss={projectsModal.hide}
                />
            )}

            {onboardingModal.visible && (
                <WorkspaceOnboardingModal
                    onCreate={fetchData}
                    onDismiss={onboardingModal.hide}
                />
            )}

            <Sidebar
                left
                column
                border={hideNav ? 'none' : undefined}
                bg="var(--f-color-background)"
                justifyContent="flex-start"
                gap="0.75rem"
                p="1rem 0"
                width="4rem"
                className="dock"
                style={{
                    '--f-button-background-color-hover': 'var(--f-color-surface-stronger)',
                    '--f-image-background-color': 'var(--f-color-image-background)',
                    '--f-avatar-color': 'var(--f-color-accent)',
                    '--f-avatar-background-color': 'var(--f-color-avatar-background)',
                    '--f-avatar-badge-outline': '0.2rem solid var(--f-color-background)',
                }}>
                {/* 
                
                <Block
                    key={workspace.image}
                    className="f-buttonize"
                    name={workspace.name}
                    src={workspace.image}
                    onClick={() => navigate(`/app/workspace/${workspace.id}/inbox`)}
                /> 
                
                <div style={{ marginBottom: '0rem' }} /> 
                
                */}

                {!hideNav && (
                    <>
                        {workspaces.map((workspace: any, index: number) => (
                            <Block
                                key={index}
                                className="f-buttonize"
                                name={workspace.name}
                                src={workspace.image}
                                onClick={() => navigate(`/app/workspace/${workspace.id}/inbox`)}
                                style={{
                                    outlineOffset: 1,
                                    outline: workspace.id == workspaceId ? '2px solid var(--f-color-accent)' : null,
                                }}
                            />
                        ))}

                        <Button
                            subtle
                            width={36}
                            height={36}
                            onClick={onboardingModal.show}>
                            <Icon icon={TbPlus} />
                        </Button>
                    </>
                )}

                {hideNav && (
                    <>
                        <Avatar
                            name={workspace.name}
                            src={workspace.image}
                            radius="var(--f-radius)"
                            imageProps={{ radius: 'var(--f-radius)' }}
                            className="f-buttonize"
                            onClick={() => navigate(`/app/workspace/${workspace.id}/inbox`)}
                        />
                        <StarredList />
                    </>
                )}

                {/* 

                <Button
                    subtle
                    width={30}
                    height={30}
                    onClick={projectsModal.show}>
                    <Icon icon={InboxIcon} />
                    <Badge
                        anchor="bottom-right"
                        variant="accent"
                        style={{ outline: '0.1rem solid var(--f-color-base-900)' }}
                    />
                </Button>
 
                <Button
                    subtle
                    width={30}
                    height={30}
                    colorToken="accent"
                    onClick={projectsModal.show}>
                    <Icon
                        icon={TbChecklist}
                        size="lg"
                    />
                </Button>
                
                <Button
                    subtle
                    width={30}
                    height={30}
                    onClick={projectsModal.show}>
                    <Icon icon={ChatBubbleLeftIcon} />
                    <Badge
                        anchor="bottom-right"
                        variant="accent"
                        style={{ outline: '0.1rem solid var(--f-color-base-900)' }}
                    />
                </Button> 
                
                <Button
                    subtle
                    width={36}
                    height={36}
                    onClick={projectsModal.show}>
                    <Icon icon={TbLayoutGrid} />
                </Button>

                <Button
                    subtle
                    width={36}
                    height={36}
                    onClick={workspaceMembersModal.show}>
                    <Icon icon={TbUserSquareRounded} />
                </Button>

                <Button
                    subtle
                    width={36}
                    height={36}
                    onClick={labelsModal.show}>
                    <Icon icon={TbTag} />
                </Button>

                <Button
                    subtle
                    width={36}
                    height={36}
                    onClick={workspaceModal.show}>
                    <Icon icon={TbSettings} />
                </Button>
 
                <Button
                    subtle
                    width={30}
                    height={30}>
                    <Icon icon={MagnifyingGlassIcon} />
                </Button>
                
                <Popover
                    arrow
                    focusTrap
                    anchor="middle-right"
                    width="fit-content"
                    portal={Portal}
                    isVisible={archives.visible}
                    onDismiss={archives.hide}
                    content={
                        <div
                            onMouseDown={stopEvent}
                            onClick={stopEvent}>
                            <Loading loading={loading} />
                            <Stack
                                spacing="0.8rem"
                                direction="vertical"
                                width={250}
                                p="1.5rem"
                                style={{
                                    maxHeight: 300,
                                    overflowY: 'auto',
                                }}>
                                {!!projects.length && (
                                    <Heading
                                        as="h6"
                                        fontWeight={600}
                                        colorToken="text-weakest">
                                        Archived Projects
                                    </Heading>
                                )}
                                {projects.map((project, index) => (
                                    <View
                                        row
                                        width="100%"
                                        key={index}>
                                        <Text
                                            className="f-buttonize"
                                            onClick={() => {
                                                archives.hide()
                                                navigate(`/app/workspace/${workspaceId}/c/${project.id}/list`)
                                            }}
                                            flex={1}>
                                            {project.name}
                                        </Text>
                                        <Button
                                            className="f-buttonize"
                                            size="sm"
                                            height="1.25rem"
                                            p="0 0.5rem"
                                            onClick={() => {
                                                archives.hide()
                                                handleRestore(project)
                                            }}
                                            variant="highlight">
                                            Restore
                                        </Button>
                                    </View>
                                ))}
                                {!projects.length && (
                                    <Text colorToken="text-weaker">You have no archived projects.</Text>
                                )}
                            </Stack>
                        </div>
                    }>
                    <Button
                        subtle
                        width={36}
                        height={36}
                        onClick={() => {
                            fetchArchivedProjects()
                            archives.show()
                        }}>
                        <Icon icon={TbArchive} />
                    </Button>
                </Popover> 

                <Popover
                    arrow
                    focusTrap
                    anchor="middle-right"
                    width={250}
                    portal={Portal}
                    isVisible={add.visible}
                    onDismiss={add.hide}
                    content={
                        <View width="100%">
                            <SlidePanel panel={panel}>
                                <Menu
                                    shadow="none"
                                    width="100%"
                                    border="none">
                                    <MenuHeading>Create New</MenuHeading>
                                    <MenuItem
                                        prefix={<Icon icon={CheckCircleIcon} />}
                                        onClick={() => {
                                            //onAddTeam()
                                            //add.hide()
                                            setType('team')
                                            setPanel(1)
                                        }}>
                                        Task
                                    </MenuItem>
                                    <MenuItem
                                        prefix={<Icon icon={QueueListIcon} />}
                                        onClick={() => {
                                            //onAddProject()
                                            //add.hide()
                                            setType('project')
                                            setPanel(1)
                                        }}>
                                        Project
                                    </MenuItem>
                                    <MenuItem
                                        prefix={<Icon icon={TbUserSquareRounded} />}
                                        onClick={() => {
                                            //onAddTeam()
                                            //add.hide()
                                            setType('team')
                                            setPanel(1)
                                        }}>
                                        Team
                                    </MenuItem>
                                    <MenuItem
                                        prefix={<Icon icon={CubeTransparentIcon} />}
                                        onClick={() => {
                                            //onAddTeam()
                                            //add.hide()
                                            setType('team')
                                            setPanel(1)
                                        }}>
                                        Workspace
                                    </MenuItem>
                                </Menu>

                                <View>
                                    <Stack
                                        p="1rem"
                                        width="100%"
                                        direction="vertical"
                                        spacing="0.75rem">
                                        <Label
                                            fontWeight={600}
                                            colorToken="accent"
                                            size="sm">
                                            {type == 'team' ? 'Team name' : 'Project name'}
                                        </Label>

                                        <InputControl>
                                            <InputPrefix>
                                                <ColorButton
                                                    color={color}
                                                    onChange={setColor}
                                                    colors={COLORS}
                                                />
                                            </InputPrefix>
                                            <Input
                                                autoFocus
                                                placeholder={type == 'team' ? 'Customer Success' : 'Website Launch'}
                                                value={name.value}
                                                onChange={name.onChange}
                                            />
                                        </InputControl>
                                    </Stack>

                                    <Footer p="0.5rem">
                                        <Button
                                            size="sm"
                                            onClick={() => setPanel(0)}>
                                            Back
                                        </Button>
                                        <Flexer />
                                        <Button
                                            variant="accent"
                                            size="sm"
                                            disabled={!name.value}>
                                            Create
                                        </Button>
                                    </Footer>
                                </View>
                            </SlidePanel>
                        </View>
                    }>
                    <View>
                         <Icon
                            style={{ borderRadius: 50, marginTop: 5 }}
                            onClick={add.show}
                            size="lg"
                            icon={PiPlusCircleFill}
                            color="var(--f-color-accent)"
                            className="f-buttonize-outline"
                        />
                    </View>
                </Popover>

                <div style={{ height: 1 }} />

                <Divider />

                <div style={{ height: 10 }} />

                <Avatar
                    className="f-buttonize-outline"
                    size="sm"
                    presence="online"
                    name={user.name}
                    style={{ outlineOffset: 3, marginBottom: '1rem' }}
                />

                <Avatar
                    className="f-buttonize-outline"
                    size="sm"
                    src="https://avatar.iran.liara.run/public/10"
                    name={user.name}
                    style={{ outlineOffset: 3, marginBottom: '1rem' }}
                />

                <Avatar
                    className="f-buttonize-outline"
                    presence="busy"
                    size="sm"
                    src="https://avatar.iran.liara.run/public/12"
                    name={user.name}
                    style={{ outlineOffset: 3, marginBottom: '1rem' }}
                />


                <Popover
                    arrow
                    width={275}
                    anchor="top-left"
                    isVisible={profileMenu.visible}
                    onDismiss={profileMenu.hide}
                    content={
                        <AccountMenu
                            user={user}
                            onAccountModalOpen={accountModal.show}
                        />
                    }>
                    <View
                        row
                        className="f-buttonize"
                        onClick={accountModal.show}>
                        <Avatar
                            presence="online"
                            size="sm"
                            src={user.image}
                            name={user.name}
                            key={user.image}
                        />
                    </View>
                </Popover>

                */}

                <Flexer />

                <div style={{ flexShrink: 0 }}>
                    {/* {!!user?.id && (
                        <PresenceAvatar
                            userId={user.id}
                            onClick={accountModal.show}
                            className="f-buttonize"
                            src={user.image}
                            name={user.name}
                            key={user.image}
                            border="none"
                        />
                    )} */}
                    <LogoNew scale={0.3} color="var(--f-color-text-weakest)" />
                </div>
            </Sidebar>
        </>
    )
}
